import React from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import { dateFieldDefault, pastDateDefault, requiredValidator } from "@/src/services/validationRules";
import MenuItem from "@mui/material/MenuItem";
import { translate } from "@/src/services/translations/translations";
import { useForm } from "@/src/provider/Form/FormProvider";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Group from "@/src/components/Wizard/Group";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import ValidatorDateField from "@/src/components/FormFields/ValidatorDateField";
import NumberFieldConfigured from "@/src/components/Form/ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import { ContractDetailsProps } from "@/src/types/ContractDetailsProps";
import wizardStyle from "@/src/components/Form/wizardStyle";

export default function TravelContractDetails({ submitProps, product }: ContractDetailsProps) {
  const { values, handleSubmit } = useForm();
  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Vertrag
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Details zu Ihrem Vertrag</strong>
              </FormLegend>
            </Grid>
            <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
              <FormLegend>Was für ein Problem gibt es bei Ihrer Reise?</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorSelectField label="Problem mit Reise" name="reasonForDispute" validators={[requiredValidator("Bitte geben Sie Ihr Problem an")]}>
                <MenuItem value="cancelled">{translate(`${product.name}.reasonForDispute.options.cancelled.label`, values)}</MenuItem>
                <MenuItem value="participationImpossible">
                  {translate(`${product.name}.reasonForDispute.options.participationImpossible.label`, values)}
                </MenuItem>
                <MenuItem value="notAsAdvertised">{translate(`${product.name}.reasonForDispute.options.notAsAdvertised.label`, values)}</MenuItem>
                <MenuItem value="other">{translate(`${product.name}.reasonForDispute.options.other.label`, values)}</MenuItem>
              </ValidatorSelectField>
            </Grid>
            <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
              <FormLegend>Bitte teilen Sie uns hier mehr Details zu Ihrer Buchung mit</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                label="Buchungs- oder Kundennummer"
                name="contractNumber"
                helperText="Bitte geben Sie hier die Buchungs- oder Kundennummer Ihrer Reise an. Sie finden diese meist in der Buchungsbestätigung oder in Ihren Reiseunterlagen."
                validators={[requiredValidator("Bitte geben Sie die Buchungs- oder Kundennummer an")]}
              />
            </Grid>
            <Grid size={12}>
              <ValidatorDateField
                label="Tag der Buchung"
                name="bookingDate"
                validators={[requiredValidator("Bitte geben Sie das Datum an"), dateFieldDefault, pastDateDefault]}
              />
            </Grid>
            <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
              <FormLegend>Von wann bis wann haben Sie die Reise gebucht?</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorDateField
                label="Start der Reise"
                name="journeyStart"
                validators={[requiredValidator("Bitte geben Sie das Datum an"), dateFieldDefault]}
              />
            </Grid>
            <Grid size={12}>
              <ValidatorDateField label="Ende der Reise" name="journeyEnd" validators={[requiredValidator("Bitte geben Sie das Datum an"), dateFieldDefault]} />
            </Grid>
            {values.reasonForDispute === "cancelled" && (
              <>
                <Grid size={12}>
                  <Group
                    type="radio"
                    name="contractualPartnerHasCancelled"
                    question="Hat Ihr Reiseanbieter die Reise von sich aus abgesagt?"
                    options={[
                      {
                        labelText: "Ja",
                        labelIcon: <CheckIcon />,
                        labelIconActive: <CheckIcon active />,
                        value: "yes",
                      },
                      {
                        labelText: "Nein",
                        labelIcon: <CrossIcon />,
                        labelIconActive: <CrossIcon active />,
                        value: "no",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>
                {values.contractualPartnerHasCancelled === "yes" && (
                  <>
                    <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
                      <FormLegend>Wann hat Ihr Reiseanbieter die Reise abgesagt?</FormLegend>
                    </Grid>
                    <Grid size={12}>
                      <ValidatorDateField
                        name="contractualPartnerCancellationDate"
                        label="Reise abgesagt am"
                        validators={[requiredValidator("Bitte geben Sie das Datum an"), dateFieldDefault, pastDateDefault]}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
            <Grid size={12}>
              <Group
                type="radio"
                name="buyerPaidStatus"
                question="Haben Sie Ihrem Reiseanbieter die Reise bereits vollständig bezahlt?"
                options={[
                  {
                    labelText: translate(`${product.name}.buyerPaidStatus.options.fully.label`, values),
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "fully",
                  },
                  {
                    labelText: translate(`${product.name}.buyerPaidStatus.options.partially.label`, values),
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "partially",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
              <FormLegend>Wann haben Sie die (letzte) Zahlung veranlasst?</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorDateField
                name="buyerPaidDate"
                label="(Letzte) Zahlung am"
                validators={[requiredValidator("Bitte geben Sie das Datum an"), dateFieldDefault, pastDateDefault]}
              />
            </Grid>
            <NumberFieldConfigured caseData={product} fieldPath={"buyerPaidAmount"} fieldType={"money"} numberType={"float"} required />
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
